import { useMemo, useRef, useEffect } from 'react';
import { debounce } from '../../../utils';
import type { LiteClient as SearchClient } from 'algoliasearch/lite';

// Debounce configuration
const DEBOUNCE_MS = 500;

type SearchRequest = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  requests: readonly any[];
};

// Create a debounced search client wrapper
export const useDebounceSearchClient = (searchClient: SearchClient): SearchClient => {
  // Use ref to maintain stable reference across renders
  const pendingRequestRef = useRef<SearchRequest | null>(null);
  const searchClientRef = useRef(searchClient);

  // Update ref when searchClient changes
  searchClientRef.current = searchClient;

  // Create stable debounced function that won't be recreated on re-renders
  const debouncedSearchRef = useRef(
    debounce(() => {
      const currentRequest = pendingRequestRef.current;
      if (!currentRequest) {
        return;
      }

      pendingRequestRef.current = null;

      // Execute the search with the current request using latest searchClient
      searchClientRef.current
        .search(currentRequest.requests as any)
        .then(currentRequest.resolve)
        .catch(currentRequest.reject);
    }, DEBOUNCE_MS),
  );

  // Cleanup on unmount to prevent memory leaks
  useEffect(
    () => () => {
      debouncedSearchRef.current.cancel();
    },
    [],
  );

  // Memoize the debounced search client - only recreate if searchClient identity changes
  return useMemo(
    () => ({
      ...searchClient,
      search: (...args: any[]) =>
        new Promise((resolve, reject) => {
          pendingRequestRef.current = { resolve, reject, requests: args[0] };
          debouncedSearchRef.current();
        }),
    }),
    [searchClient],
  ) as SearchClient;
};
